import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardList, MapPin, Heart, DollarSign, ArrowLeft, Calendar, Mail, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = '/api';

const SurveyViewPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        // Fetch survey data
        const surveyRes = await axios.get(`${API}/survey/${appointmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSurvey(surveyRes.data);

        // Fetch appointment data
        const apptRes = await axios.get(`${API}/appointments/${appointmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAppointment(apptRes.data);
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error('Session expired. Please login again.');
          navigate('/admin/login');
        } else if (err.response?.status === 404) {
          setError('Survey not found for this appointment');
        } else {
          setError('Failed to load survey data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appointmentId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF4500] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#525252]">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="inline-flex items-center gap-2 bg-[#FF4500] text-white px-6 py-3 rounded-lg hover:bg-[#E03E00]"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#0A0A0A]">Pre-Call Survey</h1>
                <p className="text-sm text-[#525252]">{appointment?.lead_name}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Appointment Info Card */}
        {appointment && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#FF4500]" />
              Appointment Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium">{appointment.lead_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium">{appointment.lead_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Date & Time</p>
                  <p className="font-medium">
                    {format(parseISO(appointment.date), 'EEEE, MMMM d, yyyy')} at {appointment.time} EST
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  appointment.status === 'completed' ? 'bg-green-500' :
                  appointment.status === 'scheduled' ? 'bg-blue-500' :
                  'bg-gray-400'
                }`}></div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-medium capitalize">{appointment.status}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Survey Responses */}
        {survey && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#FF4500]" />
              Survey Responses
            </h2>

            <div className="space-y-6">
              {/* Location */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-[#FF4500]" />
                  <h3 className="font-semibold text-sm uppercase text-gray-600">Location</h3>
                </div>
                <p className="text-lg">{survey.city}, {survey.province_state}, {survey.country}</p>
              </div>

              {/* Desire for Change */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-4 h-4 text-[#FF4500]" />
                  <h3 className="font-semibold text-sm uppercase text-gray-600">Desire for Change</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#FF4500] to-[#FF6B35] rounded-full transition-all duration-500" 
                      style={{ width: `${(survey.desire_for_change || 5) * 10}%` }}
                    ></div>
                  </div>
                  <span className="text-2xl font-bold text-[#FF4500]">{survey.desire_for_change || 5}/10</span>
                </div>
              </div>

              {/* When Realized Change */}
              {survey.when_realized_change && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">When They Realized Change Was Needed</h3>
                  <p className="text-[#0A0A0A] whitespace-pre-wrap">{survey.when_realized_change}</p>
                </div>
              )}

              {/* Changes Looking For */}
              {survey.changes_looking_for && survey.changes_looking_for.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm uppercase text-gray-600 mb-3">Changes Looking For</h3>
                  <div className="flex flex-wrap gap-2">
                    {survey.changes_looking_for.map((change, idx) => (
                      <span key={idx} className="inline-block px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {change}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Income Goals */}
              {(survey.money_needed_monthly || survey.money_needed_yearly) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-[#FF4500]" />
                    <h3 className="font-semibold text-sm uppercase text-gray-600">Income Goals</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {survey.money_needed_monthly && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500">Monthly</p>
                        <p className="text-xl font-bold text-green-600">${survey.money_needed_monthly}</p>
                      </div>
                    )}
                    {survey.money_needed_yearly && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500">Yearly</p>
                        <p className="text-xl font-bold text-green-600">${survey.money_needed_yearly}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Why Online Business */}
              {survey.why_online_business && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Why Online Business</h3>
                  <p className="text-[#0A0A0A] whitespace-pre-wrap">{survey.why_online_business}</p>
                </div>
              )}

              {/* Marketing Budget */}
              {survey.marketing_budget && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Marketing Budget</h3>
                  <span className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold">
                    {survey.marketing_budget === 'other' ? survey.marketing_budget_other : survey.marketing_budget}
                  </span>
                </div>
              )}

              {/* Readiness Level */}
              {survey.readiness_level && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Readiness Level</h3>
                  <span className={`inline-block px-4 py-2 rounded-lg font-semibold ${
                    survey.readiness_level.includes('Ready') 
                      ? 'bg-green-100 text-green-800' 
                      : survey.readiness_level.includes('curious')
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {survey.readiness_level}
                  </span>
                </div>
              )}
            </div>

            {/* Back Button */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="inline-flex items-center gap-2 bg-[#FF4500] text-white px-6 py-3 rounded-lg hover:bg-[#E03E00] transition-colors font-semibold"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Dashboard
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SurveyViewPage;
